import React from 'react';
import { getScoreKey } from '../../utils/dataUtils';
import { getCategory } from '../../utils/categoryUtils';

/**
 * Modal component to display all model scores for a task
 * 
 * @param {Object} props - Component props
 * @param {Object} props.item - Data item for this row
 * @param {Array} props.modelColumns - Array of model objects
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 */
const ScoresModal = ({ item, modelColumns, isOpen, onClose }) => {
  // Helper function to determine if a model is one of the company's ASI models
  const isCompanyModel = (modelId) => {
    return modelId === 'asi1_mini' || modelId === 'asi1_mini_0225';
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 rounded-t-lg flex justify-between items-center">
          <h3 className="text-lg font-bold text-blue-800">Task #{item.id}: {item.task}</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Body with scrollable content */}
        <div className="flex-1 overflow-y-auto p-4">
          <h4 className="text-base font-bold text-gray-700 mb-3">Model Scores</h4>
          <div className="space-y-3">
            {modelColumns.map(model => {
              const scoreKey = getScoreKey(model.id);
              const score = item.scores[scoreKey];
              const scoreNum = Number(score);
              const scoreColor = 
                scoreNum >= 90 ? 'text-blue-700' : 
                scoreNum >= 80 ? 'text-blue-500' : 
                'text-gray-700';
              
              return (
                <div 
                  key={model.id} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isCompanyModel(model.id) ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`text-sm font-medium ${isCompanyModel(model.id) ? 'text-blue-800' : 'text-gray-800'}`}>
                      {isCompanyModel(model.id) && <span className="text-yellow-500 mr-1">★</span>}
                      {model.name}
                      {isCompanyModel(model.id) && <span className="text-yellow-500 ml-1">★</span>}
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${scoreColor}`}>
                    {score}%
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              <span className="font-medium">Category:</span> {getCategory(item.task)}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoresModal;
